import type { BlobConfig, BlobState, PerformanceMode } from './animatedBackgroundSlice'
import {
  type InfiniteBlobSplineSystem,
  type SplineBlob,
  createInfiniteBlobSplineSystem,
  createSplineBlob,
  getBlobPositionInfinite,
  updateBlobProgressInfinite,
} from './animationPaths'
import {
  getAnimatedBackgroundCookieState,
  getElapsedTime,
  initializeAnimatedBackgroundCookieState,
  isCookieStateValid,
} from './cookieState'

export interface AnimationEngineConfig {
  performanceMode: PerformanceMode
  speed: number
  bounds: { width: number; height: number }
}

export interface BlobPosition {
  id: string
  x: number
  y: number
  size: number
  color: string
}

interface EngineState {
  blobs: SplineBlob[]
  splineSystems: Map<string, InfiniteBlobSplineSystem>
  lastUpdate: number
  frameCount: number
}

export class AnimationEngine {
  private state: EngineState
  private config: AnimationEngineConfig
  private targetFPS: number = 60

  constructor(config: AnimationEngineConfig) {
    this.config = config
    this.state = {
      blobs: [],
      splineSystems: new Map(),
      lastUpdate: 0,
      frameCount: 0,
    }
    this.updateTargetFPS()
  }

  updateConfig(config: Partial<AnimationEngineConfig>): void {
    this.config = { ...this.config, ...config }
    this.updateTargetFPS()
  }

  initializeBlobs(blobConfigs: BlobConfig[], savedStates?: BlobState[]): void {
    this.clearBlobs()

    initializeAnimatedBackgroundCookieState()

    if (savedStates && savedStates.length > 0) {
      this.state.blobs = savedStates.map((savedBlob) => ({
        id: savedBlob.id,
        seed: savedBlob.seed,
        positionProgress: savedBlob.pathProgress,
        positionSegment: savedBlob.pathSegment,
        speedProgress: savedBlob.speedProgress ?? savedBlob.pathProgress,
        speedSegment: savedBlob.speedSegment ?? Math.floor(savedBlob.pathSegment / 2),
        baseSpeed: savedBlob.speed,
        speedVariance: 0.5,
        currentSpeed: savedBlob.speed,
        size: savedBlob.size,
        color: savedBlob.color,
      }))

      savedStates.forEach((blob) => {
        const splineSystem = createInfiniteBlobSplineSystem(blob.seed, this.config.bounds)
        this.state.splineSystems.set(blob.id, splineSystem)
      })
    } else {
      const cookieState = getAnimatedBackgroundCookieState()
      const blobCount = Math.min(blobConfigs.length, this.getMaxBlobsForPerformanceMode())
      const colorOptions = blobConfigs.map((config) => config.color)

      for (let i = 0; i < blobCount; i++) {
        const config = blobConfigs[i % blobConfigs.length]
        let blob: SplineBlob

        if (cookieState && isCookieStateValid(cookieState)) {
          const elapsedTime = getElapsedTime(cookieState)
          blob = createSplineBlob(
            `blob-${i}`,
            this.config.bounds,
            colorOptions,
            cookieState.masterSeed,
            elapsedTime
          )
        } else {
          blob = createSplineBlob(`blob-${i}`, this.config.bounds, colorOptions)
        }

        if (config.size) {
          blob.size = config.size + (Math.random() * 40 - 20)
        }

        this.state.blobs.push(blob)

        const splineSystem = createInfiniteBlobSplineSystem(
          blob.seed,
          this.config.bounds,
          blob.speedVariance
        )
        this.state.splineSystems.set(blob.id, splineSystem)
      }
    }
  }

  update(currentTime: number): BlobPosition[] {
    const frameInterval = 1000 / this.targetFPS

    if (currentTime - this.state.lastUpdate < frameInterval) {
      return this.getCurrentPositions()
    }

    const deltaTime = currentTime - this.state.lastUpdate
    this.state.lastUpdate = currentTime
    this.state.frameCount++

    this.state.blobs = this.state.blobs.map((blob) => {
      const splineSystem = this.state.splineSystems.get(blob.id)
      if (!splineSystem) return blob

      return updateBlobProgressInfinite(blob, splineSystem, deltaTime, this.config.speed * 10)
    })

    return this.getCurrentPositions()
  }

  getCurrentPositions(): BlobPosition[] {
    return this.state.blobs
      .map((blob) => {
        const splineSystem = this.state.splineSystems.get(blob.id)
        if (!splineSystem) return null

        const position = getBlobPositionInfinite(blob, splineSystem)
        return {
          id: blob.id,
          x: position.x,
          y: position.y,
          size: blob.size,
          color: blob.color,
        }
      })
      .filter((pos): pos is BlobPosition => pos !== null)
      .filter((pos) => this.isInViewport(pos))
  }

  getBlobStates(): BlobState[] {
    return this.state.blobs.map((blob) => ({
      id: blob.id,
      seed: blob.seed,
      pathProgress: blob.positionProgress,
      pathSegment: blob.positionSegment,
      speedProgress: blob.speedProgress,
      speedSegment: blob.speedSegment,
      speed: blob.baseSpeed,
      size: blob.size,
      color: blob.color,
    }))
  }

  getDebugInfo() {
    const splineDebugInfo = new Map()
    const speedDebugInfo = new Map()
    const blobPositions = new Map()

    this.state.splineSystems.forEach((system, blobId) => {
      splineDebugInfo.set(blobId, system.positionSpline.getDebugInfo())
      speedDebugInfo.set(blobId, system.speedSpline.getDebugInfo())
    })

    this.getCurrentPositions().forEach((pos) => {
      blobPositions.set(pos.id, { x: pos.x, y: pos.y })
    })

    return {
      blobs: this.state.blobs,
      splinePaths: splineDebugInfo,
      speedSplines: speedDebugInfo,
      blobPositions,
      dimensions: this.config.bounds,
      frameCount: this.state.frameCount,
    }
  }

  updateBlobColors(newColors: BlobConfig[]): void {
    this.state.blobs = this.state.blobs.map((blob, index) => ({
      ...blob,
      color: newColors[index]?.color || blob.color,
    }))
  }

  private clearBlobs(): void {
    this.state.blobs = []
    this.state.splineSystems.clear()
  }

  private updateTargetFPS(): void {
    switch (this.config.performanceMode) {
      case 'high':
        this.targetFPS = 60
        break
      case 'medium':
        this.targetFPS = 30
        break
      case 'low':
        this.targetFPS = 15
        break
      default:
        this.targetFPS = 60
    }
  }

  private getMaxBlobsForPerformanceMode(): number {
    switch (this.config.performanceMode) {
      case 'high':
        return 5
      case 'medium':
        return 4
      case 'low':
        return 3
      default:
        return 0
    }
  }

  private isInViewport(pos: BlobPosition): boolean {
    const margin = 100
    const radius = pos.size / 2

    return (
      pos.x + radius + margin > 0 &&
      pos.x - radius - margin < this.config.bounds.width &&
      pos.y + radius + margin > 0 &&
      pos.y - radius - margin < this.config.bounds.height
    )
  }
}
